import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createGame } from '@/integrations/supabase/game/gameCreateService';
import { useAuth } from '@/context/AuthContext';

// Module-level guards that survive unmount/remount within the same browser session
const createdKeys = new Set<string>();
const inflightCreations = new Map<string, Promise<void>>();

/** Reset module-level state — for test cleanup only */
export const _resetRecurringGamesState = () => {
  createdKeys.clear();
  inflightCreations.clear();
};

export const useRecurringGames = () => {
  const { user } = useAuth();
  const [recurringVersion, setRecurringVersion] = useState(0);

  useEffect(() => {
    if (!user) return;

    const ensureRecurringGames = async () => {
      const currentYear = new Date().getUTCFullYear();

      // Find all recurring games where this user is a member
      const { data: userGameLinks } = await supabase
        .from('game_players')
        .select('game_id')
        .eq('player_id', user.id);

      if (!userGameLinks || userGameLinks.length === 0) return;

      const gameIds = userGameLinks.map(link => link.game_id);

      // Get all recurring games from those game IDs
      const { data: recurringGames } = await supabase
        .from('games')
        .select('*, game_players(player_id, profiles:player_id(id, name, email, image_url))')
        .eq('is_recurring', true)
        .in('id', gameIds);

      if (!recurringGames || recurringGames.length === 0) return;

      // Group by recurring_group_id
      const groups = new Map<string, typeof recurringGames>();
      for (const game of recurringGames) {
        if (!game.recurring_group_id) continue;
        const existing = groups.get(game.recurring_group_id) || [];
        existing.push(game);
        groups.set(game.recurring_group_id, existing);
      }

      let created = false;

      for (const [groupId, instances] of groups) {
        const dedupKey = `${groupId}:${currentYear}`;

        // Skip if already created in this browser session
        if (createdKeys.has(dedupKey)) continue;

        // Check if current year instance exists in the fetched data
        const hasCurrentYear = instances.some(g => {
          const year = new Date(g.start_date).getUTCFullYear();
          return year === currentYear;
        });

        if (hasCurrentYear) {
          createdKeys.add(dedupKey);
          continue;
        }

        // If another call is already creating this group+year, wait for it
        const existing = inflightCreations.get(dedupKey);
        if (existing) {
          await existing;
          continue;
        }

        // Create a promise for this group+year and store it
        const creationPromise = (async () => {
          // Double-check DB directly to prevent duplicates from concurrent tabs
          const { data: existingForYear } = await supabase
            .from('games')
            .select('id')
            .eq('recurring_group_id', groupId)
            .gte('start_date', `${currentYear}-01-01`)
            .lte('start_date', `${currentYear}-12-31`)
            .limit(1);

          if (existingForYear && existingForYear.length > 0) {
            createdKeys.add(dedupKey);
            return;
          }

          // Use most recent instance as template
          const sorted = [...instances].sort((a, b) =>
            new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
          );
          const template = sorted[0];

          // Extract players from template
          interface GamePlayerProfile {
            player_id: string;
            profiles: { id: string; name: string | null; email: string | null; image_url: string | null } | null;
          }
          const players = (template.game_players as GamePlayerProfile[] || []).map((gp) => {
            const p = gp.profiles;
            return {
              id: p?.id || gp.player_id,
              name: p?.name || 'Unknown',
              email: p?.email || null,
              imageUrl: p?.image_url || null,
            };
          });

          // Derive name: replace year suffix or append current year
          const baseName = template.name.replace(/\s+\d{4}$/, '');
          const newName = `${baseName} ${currentYear}`;

          const result = await createGame({
            name: newName,
            courseId: template.course_id,
            players,
            bet: template.bet || undefined,
            durationType: 'yearly',
            isRecurring: true,
            recurringGroupId: groupId,
            startDate: `${currentYear}-01-01T00:00:00.000Z`,
            endDate: `${currentYear}-12-31T23:59:59.999Z`,
          });

          if (!result || result.error) {
            // Unique constraint violation means another tab/mount already created it — not an error
            const errCode = (result?.error as { code?: string })?.code;
            if (errCode === '23505') {
              createdKeys.add(dedupKey);
              return;
            }
            console.error(`useRecurringGames - Failed to create ${newName}:`, result?.error);
            // Don't add to createdKeys so next mount can retry
            return;
          }

          createdKeys.add(dedupKey);
          created = true;
        })();

        inflightCreations.set(dedupKey, creationPromise);
        try {
          await creationPromise;
        } finally {
          inflightCreations.delete(dedupKey);
        }
      }

      if (created) {
        setRecurringVersion(v => v + 1);
      }
    };

    ensureRecurringGames();
  }, [user]);

  return { recurringVersion };
};
